import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { statsAPI } from '../services/api';
import { Banner, BannerType, TYPE_ICONS, TYPE_LABELS } from './admin/bannerUtils';
import '../styles/AnnouncementBanner.css';

// Fired by the admin Banners views after a change so the live banners refresh without a reload
export const BANNERS_CHANGED_EVENT = 'banners-changed';

// Exposes the stack height so the sticky header (and admin sidebar) sit just below it
const HEIGHT_VAR = '--announcement-banner-height';

interface AnnouncementBannerViewProps {
  heading: string;
  subheading?: string | null;
  type: BannerType;
  /** Shows a close button when provided */
  onDismiss?: () => void;
}

/**
 * Presentational banner - also used by the admin Banners view as a live preview.
 */
export function AnnouncementBannerView({
  heading,
  subheading,
  type,
  onDismiss,
}: AnnouncementBannerViewProps) {
  return (
    <div
      className={`announcement-banner announcement-banner-${type}`}
      role={type === 'critical' ? 'alert' : 'status'}
      data-testid="announcement-banner"
    >
      <div className="announcement-banner-inner">
        <FontAwesomeIcon
          icon={TYPE_ICONS[type]}
          className="announcement-banner-icon"
          title={TYPE_LABELS[type]}
        />
        <div className="announcement-banner-message">
          <p className="announcement-banner-heading">{heading}</p>
          {subheading && <p className="announcement-banner-subheading">{subheading}</p>}
        </div>
        {onDismiss && (
          <button
            type="button"
            className="announcement-banner-close"
            onClick={onDismiss}
            aria-label="Dismiss announcement"
            data-testid="announcement-banner-dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * AnnouncementBanner - Fetches active banners and shows them stacked above the app header.
 * Hides each one automatically once its time window ends. Dismissible banners stay hidden
 * for the rest of the login session (tracked server-side in the session).
 */
function AnnouncementBanner() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const stackRef = useRef<HTMLDivElement>(null);

  const loadBanners = useCallback((signal?: AbortSignal) => {
    statsAPI
      .getBanners(signal)
      .then((response): void => {
        setBanners(response.data?.data || []);
      })
      .catch((): void => {
        // Banners are non-critical - fail silently
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadBanners(controller.signal);
    const handleChanged = () => loadBanners();
    window.addEventListener(BANNERS_CHANGED_EVENT, handleChanged);
    return () => {
      controller.abort();
      window.removeEventListener(BANNERS_CHANGED_EVENT, handleChanged);
    };
  }, [loadBanners]);

  // Remove banners whose window closes while the page is open
  useEffect(() => {
    const endTimes = banners
      .filter((b) => b.end_at)
      .map((b) => new Date(b.end_at as string).getTime());
    if (endTimes.length === 0) return;
    const msUntilNextEnd = Math.min(...endTimes) - Date.now();
    // setTimeout overflows beyond ~24.8 days; skip scheduling for far-off end times
    if (msUntilNextEnd > 2 ** 31 - 1) return;
    const timer = setTimeout(
      () => setBanners((prev) => prev.filter((b) => !b.end_at || new Date(b.end_at) > new Date())),
      Math.max(0, msUntilNextEnd)
    );
    return () => clearTimeout(timer);
  }, [banners]);

  // Keep the CSS height variable in sync with the rendered stack
  useLayoutEffect(() => {
    const root = document.documentElement;
    const stack = stackRef.current;
    if (!stack) {
      root.style.setProperty(HEIGHT_VAR, '0px');
      return;
    }
    const update = () => root.style.setProperty(HEIGHT_VAR, `${stack.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stack);
    return () => {
      observer.disconnect();
      root.style.setProperty(HEIGHT_VAR, '0px');
    };
  }, [banners.length]);

  const handleDismiss = (id: number) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
    statsAPI.dismissBanner(id).catch((): void => {
      // Dismissal is best-effort - the banner is already hidden for this page view
    });
  };

  if (banners.length === 0) return null;

  return (
    <div className="announcement-banner-stack" ref={stackRef} data-testid="announcement-banners">
      {banners.map((banner) => (
        <AnnouncementBannerView
          key={banner.id}
          heading={banner.heading}
          subheading={banner.subheading}
          type={banner.type}
          onDismiss={banner.non_dismissible ? undefined : () => handleDismiss(banner.id)}
        />
      ))}
    </div>
  );
}

export default AnnouncementBanner;
